import { useState } from 'react'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import environment from 'environment'

import { DeckCardInfoType } from 'types/search'
import { DEFAULT_DECK_CARD_IMAGE } from 'types/deck'
import { ROLES } from 'types/user'

import { useGeneratePatreonLinkInfo } from 'hooks/generatePatreonLinks'
import { useActionless, useAppSelector } from 'app-redux/hooks'
import { SET_ACTIVE_STATE } from 'app-redux/active/actions/types'

import WebsocketService from 'services/websocket.service'
import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'
import CookieService from 'services/cookie.service'

import AvatarCircle from 'components/user/AvatarCircle'
import Link from 'components/elements/Link'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import Image from 'components/elements/Image'
import ContextMenu from 'components/elements/ContextMenu'

import Analytics from 'utils/googleAnalytics'
import { PUBLIC_ROUTES } from 'utils/token.utils'
import { encodeUsernameRoute } from 'utils/encoding'
import { generateDeckUrl } from 'utils/deckSeo'
import { useBuildCollectionUrl } from 'utils/queryParams/collection.queryParams'

import styles from './accountDropdown.module.scss'

type Props = {
  className?: string
}

const AccountDropdown = ({ className }: Props) => {
  const router = useRouter()

  const [_confirmSignOut, setConfirmSignOut] = useState(false)

  const [loading, setLoading] = useState(false)
  const [userDecks, setUserDecks] = useState(new Array<DeckCardInfoType>())
  const [userDecksFetched, setUserDecksFetched] = useState(false)

  const patreonLink = useGeneratePatreonLinkInfo('accountdropdown')

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const [{ tbUser: username, tbRootFolder: rootFolder, tbR: roles, tbAvatar: avatar }] = useCookies([
    'tbUser',
    'tbRootFolder',
    'tbR',
    'tbAvatar',
  ])

  const saving = useAppSelector(state => state.deck.saving)
  const isMobile = useAppSelector(state => state.active.isMobile)

  const collectionUrl = useBuildCollectionUrl()

  const handleSignOut = () => {
    if (saving !== 'saved') return setConfirmSignOut(true)

    CookieService.logout()

    if (!router.pathname.includes('account')) router.reload()
    else router.push('/')
  }

  const handleFetchUserDecks = () => {
    if (userDecksFetched) return

    setLoading(true)

    Analytics.signalEvent('Account dropdown opened')

    DeckService.getMyDecks()
      .then(res => {
        setUserDecks(res.results)
        setUserDecksFetched(true)
      })
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  const isMod = roles.includes(ROLES.MOD)
  const hideOnNonPublicRoutes = environment.runningOnPrivateServer && PUBLIC_ROUTES.includes(router.pathname)

  return (
    <ContextMenu
      openOnLeftClick
      openMenuAsDrawerOnMobile
      maxInitialOpenHeight={75}
      onFocus={handleFetchUserDecks}
      menuClassName={styles.dropdownMenu}
      triggerClassName={className}
      options={[
        { label: 'New deck', icon: 'add', to: '/new-deck', hidden: hideOnNonPublicRoutes },
        { label: 'New card package', icon: 'cube', to: '/new-package', hidden: hideOnNonPublicRoutes },
        { label: 'Deck sandbox', icon: 'flask', to: '/sandbox', hidden: hideOnNonPublicRoutes },
        { type: 'spacer', hidden: patreonLink.hidden },
        { ...patreonLink },
        { type: 'spacer', hidden: hideOnNonPublicRoutes },
        {
          label: 'Search my decks',
          icon: 'sliders horizontal',
          to: `/search/decks/?ownerUsername=${username}`,
          hidden: hideOnNonPublicRoutes,
        },
        { type: 'spacer' },
        {
          label: 'My decks',
          icon: 'folder outline',
          to: `/folders/${rootFolder}/`,
          hidden: hideOnNonPublicRoutes,
        },
        {
          label: 'My collection',
          icon: 'address book outline',
          to: collectionUrl,
          hidden: hideOnNonPublicRoutes,
        },
        {
          label: 'My other stuff',
          type: 'extras',
          icon: 'user circle outline',
          id: 'my-overflow',
          extraTriggerClassName: styles.overrideGlobalToolbarStyling,
          options: [
            {
              label: 'Followers / Following',
              icon: 'users',
              onClick: () => setActiveState({ modals: { following: true } }),
              hidden: hideOnNonPublicRoutes,
            },
            {
              label: 'Card packages',
              icon: 'cubes',
              to: `/search/decks/?ownerUsername=${username}&packages=true`,
              hidden: hideOnNonPublicRoutes,
            },

            {
              label: 'Personal categories',
              to: '/personal-categories',
              icon: 'globe',
              hidden: hideOnNonPublicRoutes,
            },
            {
              label: 'Custom cards',
              to: '/custom-cards',
              icon: 'file image outline',
              hidden: hideOnNonPublicRoutes,
            },
            { type: 'spacer' },
            {
              label: 'Public user page',
              icon: 'user',
              to: `/u/${encodeUsernameRoute(username)}`,
              hidden: hideOnNonPublicRoutes,
            },
            {
              label: 'Account settings',
              icon: 'cog',
              to: '/accountSettings/site-settings',
              hidden: hideOnNonPublicRoutes,
            },
            { type: 'spacer', hidden: !isMod },
            {
              label: 'Moderator settings',
              icon: 'superscript',
              to: '/moderation',
              className: styles.mod,
              hidden: !isMod,
            },
          ],
        },

        { type: 'spacer', hidden: hideOnNonPublicRoutes },
        {
          hidden: !loading,
          label: (
            <div className={styles.decksLoading}>
              <SimpleSpinner size="xSmall" />
              Fetching your decks...
            </div>
          ),
          onClick: () => null,
          disabled: true,
        },
        {
          type: 'spacer',
          hidden: !loading,
        },
        { type: 'header', label: 'My recent decks', hidden: !isMobile || loading },
        {
          type: 'custom',
          hidden: (hideOnNonPublicRoutes && !userDecksFetched) || loading,
          className: styles.deckList,
          customChild: (
            <>
              {userDecks.map(deck => (
                <Link
                  key={deck.id}
                  href={generateDeckUrl(deck.id, deck.name)}
                  // For some reason when transitioning from /deck/:arbitrary-id => /deck/:arbitrary-id2 the reload happens prior to the useEffect cleanup being called on the deck page
                  // This results in the initial websocket connection remaining open (sometimes, I think it may be a timing thing)
                  // I think this is a next issue, where it's not calling the useEffect cleanup until _after_ the page transition, at which point we no longer have a reference to the initial websocket connection
                  // Calling uninitialize here (just prior to the page transition) to prevent this behavior from happening
                  // This is most certainly a bandaid - ideally it would call the useEffect cleanup with the correct timing
                  onClick={() => WebsocketService.uninitialize()}>
                  <Image
                    alt={deck.name}
                    className={styles.previewImage}
                    src={deck.customFeatured || deck.featured || DEFAULT_DECK_CARD_IMAGE}
                  />
                  <span>{deck.name}</span>
                </Link>
              ))}
            </>
          ),
        },
        { type: 'spacer', hidden: hideOnNonPublicRoutes || userDecks.length === 0 },
        { label: 'Sign out', icon: 'sign out', onClick: handleSignOut },
      ]}>
      <button className={styles.dropdownTrigger}>
        <AvatarCircle
          src={avatar || 'https://storage.googleapis.com/topdekt-user/avatars/default/avatar_colorless.svg'}
        />
      </button>
    </ContextMenu>
  )
}

export default AccountDropdown
